<template>
    <div class="modal-container-rule modern-rule-modal">
        <div class="modal-header enhanced-header">
            <div class="title-block">
                <md-icon class="title-icon">tune</md-icon>
                <div>
                    <h4 class="modal-title">Rule Condition</h4>
                    <p class="subtitle">Build conditions for calculating rates. Select a variable, pick an operator and add values.</p>
                </div>
            </div>
            <md-button class="md-simple md-just-icon md-round close-btn" @click.stop="$modal.hide">
                <md-icon>clear</md-icon>
            </md-button>
        </div>

        <div class="modal-body">
            <form-wrapper class="form-wrapper" :validator="$v.condition">
                <div class="condition-form-grid" :class="{ 'zone-active': isAddressVariable, 'has-between': condition.selectedOption && condition.selectedOption.key==='Between', 'has-operator': condition.length>0, 'no-operator': !condition.length }">
                    <div v-if="condition.length" class="form-segment small operator-wrapper">
                        <form-group name="selectedOperator" label="And / Or" attribute="Logical Operator">
                            <md-input v-model="condition.selectedOperator" type="hidden" required />
                            <vue-select
                                placeholder="Operator"
                                class="select-value-box"
                                :options="operators"
                                label="key"
                                v-model="condition.selectedOperator"
                            >
                            </vue-select>
                        </form-group>
                    </div>

                    <!-- Variable -->
                    <div class="form-segment medium variable-segment">
                        <form-group name="selectedVariable" label="Variable" attribute="Variable">
                            <md-input
                                v-model="condition.selectedVariable"
                                type="hidden"
                                required
                                @input="variableChanged"
                            />
                            <vue-select
                                class="select-value-box"
                                placeholder="Variable"
                                :options="conditionVariables"
                                label="key"
                                v-model="condition.selectedVariable"
                                :title="condition.selectedVariable ? condition.selectedVariable.key : 'Variable'"
                                append-to-body
                            >
                            </vue-select>
                        </form-group>
                    </div>

                    <!-- Condition Type -->
                    <div class="form-segment medium condition-segment">
                        <form-group
                            v-if="
                                condition.selectedVariable !== undefined &&
                                condition.selectedVariable !== null &&
                                condition.selectedVariable.key === 'Tag'"
                            name="tagValue"
                            label="Tag"
                            attribute="Tag"
                        >
                            <md-input
                                v-model="condition.tagValue"
                                type="hidden"
                                required
                            />
                            <vue-select
                                class="select-value-box"
                                placeholder="Tag"
                                :options="teamTags"
                                label="name"
                                v-model="condition.tagValue"
                                append-to-body
                            >
                            </vue-select>
                        </form-group>
                        <form-group
                            v-else-if="
                                condition.selectedVariable !== undefined &&
                                    condition.selectedVariable !== null &&
                                    (condition.selectedVariable.key !== 'Pickup Address' &&
                                        condition.selectedVariable.key !== 'Drop Address')
                            "
                            name="selectedOption"
                            label="Condition"
                            attribute="Condition"
                        >
                            <md-input
                                v-model="condition.selectedOption"
                                type="hidden"
                                required
                            />
                            <vue-select
                                class="select-value-box"
                                placeholder="Condition"
                                :options="conditionTypes"
                                label="key"
                                v-model="condition.selectedOption"
                                append-to-body
                            >
                            </vue-select>
                        </form-group>
                        <form-group
                            v-else
                            name="selectedOption"
                            label="Condition"
                            attribute="Condition"
                        >
                            <md-input
                                v-model="condition.selectedOption"
                                type="hidden"
                                required
                            />
                            <vue-select
                                class="select-value-box"
                                placeholder="Condition"
                                :options="addressZoneConditionTypes"
                                label="key"
                                v-model="condition.selectedOption"
                                append-to-body
                            >
                            </vue-select>
                        </form-group>
                    </div>

                    <!-- Zone / Value unified width segment -->
                    <div class="form-segment value-zone-segment" :class="{ 'is-zone': isAddressVariable, 'is-value': !isAddressVariable }">
                        <form-group v-if="isAddressVariable" name="zoneValue" label="Zone" attribute="Zone" class="zone-no-label">
                            <md-input v-model.number="condition.value" type="hidden" required />
                            <multiselect
                                class="zone-select"
                                v-model="condition.zoneValue"
                                label="name"
                                track-by="name"
                                placeholder="Search zone"
                                open-direction="right"
                                :options="rateZones"
                                :searchable="true"
                                :clear-on-select="false"
                                :close-on-select="true"
                                :limit="1"
                                :limit-text="limitText"
                                :max-height="240"
                                :show-no-results="false"
                                :reduce="(zone) => zone.rateZoneId"
                                @open="zoneDropdownOpen = true"
                                @close="zoneDropdownOpen = false"
                            >
                                <template slot="tag" slot-scope="{ option }">
                                    <span class="zone_names">
                                        <span>{{ option.name }}</span>
                                    </span>
                                </template>
                                <template slot="clear" slot-scope="props">
                                    <div
                                        class="zone_names"
                                        v-if="rateZones.length"
                                        @mousedown.prevent.stop="clearAll(props.search)"
                                    ></div>
                                </template>
                                <span slot="noResult">No zones found.</span>
                            </multiselect>
                        </form-group>
                        <form-group v-else-if="
                                condition.selectedVariable !== undefined &&
                                    condition.selectedVariable !== null &&
                                    condition.selectedVariable.key !== 'Tag'"
                                name="value"
                                label="Value"
                                attribute="Value">
                            <md-input
                                class="select-value-box"
                                v-model.number="condition.value"
                                required
                            />
                        </form-group>
                    </div>

                    <!-- Between Second Value (only render when Between selected) -->
                    <div
                        v-if="condition.selectedOption && condition.selectedOption.key === 'Between' && condition.selectedVariable.key !== 'Tag'"
                        class="form-segment between-second"
                    >
                        <form-group name="valueTwo" label="Value (to)" attribute="Number" class="between-container">
                            <md-input
                                class="select-value-box"
                                v-model.number="condition.valueTwo"
                                :required="condition.selectedOption && condition.selectedOption.key==='Between'"
                            />
                        </form-group>
                    </div>

                    <!--Add Rule Button-->
                    <div class="action-segment">
                        <md-button class="md-default md-just-icon md-round add-btn" @click="addToCondition()">
                            <md-icon>add</md-icon>
                        </md-button>
                    </div>
                </div>
            </form-wrapper>

            <!-- Existing Conditions (inline at bottom) -->
            <div v-if="condition.length" class="rules-summary rules-summary-bottom">
                <div class="rules-summary-header">
                    <md-icon class="rules-summary-icon">fact_check</md-icon>
                    <span class="rules-summary-title">Current Rules</span>
                    <span class="rules-count-badge">{{ condition.length }}</span>
                </div>
                <ul class="rules-list rules-list-inline">
                    <li v-for="(oper, index) in condition" :key="index" class="rule-item rule-item-inline">
                        <span class="rule-text">{{ formatCondition(oper) }}</span>
                        <button
                            type="button"
                            class="rule-delete-btn"
                            title="Delete Condition"
                            @click.stop="deleteCondition(oper)"
                        >
                            <md-icon>close</md-icon>
                        </button>
                    </li>
                </ul>
            </div>
        </div>

        <div class="modal-footer modern-footer">
            <div class="footer-left" v-if="condition.length">
                <span class="summary-count">{{ condition.length }} condition<span v-if="condition.length>1">s</span> added</span>
            </div>
            <div class="footer-actions">
                <md-button class="md-default dialog-button" @click.stop="$modal.hide">Cancel</md-button>
                <md-button class="md-primary dialog-button" @click="create()">Save</md-button>
            </div>
        </div>
    </div>
</template>

<script>
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { handleRequests, showErrorMessage } from '@/helpers';
import { mapGetters } from 'vuex';
import { RATE_CONDITION_TYPES, RATE_VARIABLES, RATE_ZONE_CONDITION_TYPES } from '@/utils/constants';
import { decimal, required, requiredIf } from 'vuelidate/lib/validators';

export default {
    name: 'CreateConditionRuleContent',
    components: {},
    mixins: [GeneralMixin],
    props: {
        rateDetail: {
            type: Object,
            default: () => {}
        },
        conditionDetail: {
            type: Object,
            default: () => {}
        },
        resolve: {
            type: Function,
            default: () => {}
        },
        isUpdate: {
            type: Boolean,
            default: false
        },
        rateRuleId: {
            type: Number,
            default: null
        },
        rateZones: {
            type: Array,
            default: () => []
        },
        teamTags: {
            type: Array,
            default: () => []
        }
    },
    watch: {},
    data() {
        return {
            operators: [{ key: 'And', type: '&&' }, { key: 'Or', type: '||' }],
            conditionVariables: RATE_VARIABLES,
            conditionTypes: RATE_CONDITION_TYPES,
            addressZoneConditionTypes: RATE_ZONE_CONDITION_TYPES,
            selectedOption: null,
            tagValue: null,
            selectedVariable: null,
            selectedOperator: null,
            value: null,
            condition: this.rateDetail.condition,
            isListLoading: true,
            localMessages: {
                required: '{attribute} is required.'
            },
            previousVariableSelected: null,
            zoneValue: null,
            zoneDropdownOpen: false
        };
    },
    validations: {
        condition: {
            selectedVariable: {
                required
            },
            selectedOperator: {
                // eslint-disable-next-line func-names
                required: requiredIf(function() {
                    return this.condition.length >= 1;
                })
            },
            selectedOption: {
                // Don't require a standard condition option when Tag variable is selected
                // eslint-disable-next-line func-names
                required: requiredIf(function() {
                    return this.condition.selectedVariable?.key !== 'Tag';
                })
            },
            value: {
                // eslint-disable-next-line func-names
                required: requiredIf(function() {
                    if (this.condition.selectedVariable?.key === 'Tag') {
                        return false;
                    }
                    return this.condition.selectedOption?.key !== 'PickupAddress';
                }),
                decimal
            },
            tagValue: {
                // eslint-disable-next-line func-names
                required: requiredIf(function() {
                    return this.condition.selectedVariable?.key === 'Tag';
                })
            },
            valueTwo: {
                // eslint-disable-next-line func-names
                required: requiredIf(function() {
                    return this.condition.selectedOption?.key === 'Between';
                }),
                decimal
            },
            zoneValue: {
                // eslint-disable-next-line func-names
                required: requiredIf(function() {
                    return (
                        this.condition.selectedOption?.key === 'PickupAddress' ||
                        this.condition.selectedOption?.key === 'DropAddress'
                    );
                })
            }
        }
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        }),
        isAddressVariable() {
            return (
                this.condition?.selectedVariable?.key === 'Pickup Address' ||
                this.condition?.selectedVariable?.key === 'Drop Address'
            );
        },
        // Removed inlineAddressConditionActive logic to ensure identical sizing for all variable types
    },
    methods: {
        variableChanged(val) {
            if (val !== null) {
                if (val.type === 'PickupAddress' || val.type === 'DropAddress') {
                    if (this.condition.selectedOption !== null && this.condition.selectedOption !== undefined) {
                        const currentCondition = this.addressZoneConditionTypes.find(
                            (x) => x.type === this.condition.selectedOption.type
                        );

                        if (currentCondition === undefined) {
                            this.condition.selectedOption = null;
                        }
                    }

                    if (this.condition.value) {
                        const zone = this.rateZones.find((x) => x.name === this.condition.value);
                        if (zone === undefined) {
                            this.condition.value = null;
                        }
                    }
                } else {
                    if (this.condition.selectedOption !== null && this.condition.selectedOption !== undefined) {
                        const currentCondition = this.conditionTypes.find(
                            (x) => x.type === this.condition.selectedOption.type
                        );

                        if (currentCondition === undefined) {
                            this.condition.selectedOption = null;
                        }
                    }

                    if (this.condition.value) {
                        if (
                            this.previousVariableSelected === 'PickupAddress' ||
                            this.previousVariableSelected === 'DropAddress'
                        ) {
                            this.condition.value = null;
                        }
                    }
                }

                this.previousVariableSelected = val.type;
            }
        },
        clearZone() {
            this.condition.zoneValue = null;
        },
        formatCondition(conditionRow) {
            let conditionRowCopy = conditionRow;
            const numberPattern = /\d+/g;

            if (conditionRowCopy.startsWith('(')) {
                const variableName = conditionRowCopy.match(/\br\.(\w+)\b/)[1];
                const formatted = `${variableName} Between ${conditionRowCopy.match(/\d+/g)[0]} and ${
                    conditionRowCopy.match(/\d+/g)[1]
                }`;
                conditionRowCopy = formatted;
            }

            if (conditionRowCopy.includes('In') || conditionRowCopy.includes('NotIn')) {
                const match = conditionRowCopy.match(numberPattern);

                match.forEach((zoneId, index) => {
                    const zone = this.rateZones.find((x) => x.rateZoneId === Number(zoneId));
                    conditionRowCopy = conditionRowCopy.replaceAll(zoneId, zone.name);
                });
            }

            if (conditionRowCopy.includes('TagIds.Contains(')) {
                const tagId = conditionRowCopy.match(/\d+/g)[0];
                const tag = this.teamTags.find((x) => x.tagId === Number(tagId));

                conditionRowCopy = conditionRowCopy.replaceAll(tagId, `'${tag.name}''`);
                conditionRowCopy = conditionRowCopy.replace('TagIds.Contains', 'Tag is ');
                conditionRowCopy = conditionRowCopy.replaceAll('(', '');
                conditionRowCopy = conditionRowCopy.replaceAll(')', '');
            }


            conditionRowCopy = conditionRowCopy.replaceAll('r.', '');
            conditionRowCopy = conditionRowCopy.replace('&&', 'And');
            conditionRowCopy = conditionRowCopy.replace('||', 'Or');
            conditionRowCopy = conditionRowCopy.replace('==', '=');
            conditionRowCopy = conditionRowCopy.replaceAll('.', ' ');

            return conditionRowCopy;
        },
        async create() {
            const obj = {
                rateRuleId: this.rateRuleId,
                condition: this.condition
            };

            try {
                this.isListLoading = true;
                const endpoint = '/api/rates/condition';
                const payload = {
                    method: 'post',
                    data: obj
                };

                this.$_handleLoaderState(true, 'SAVING...');

                const response = await handleRequests(endpoint, payload);

                if (response.status === 200) {
                    this.$notify({
                        message: 'Successfully updated condition.',
                        type: 'success'
                    });

                    this.resolve({
                        record: response.status
                    });
                } else {
                    showErrorMessage(this, 'Failed adding condition.', null);
                }
            } catch (error) {
                const message = 'Error Creating Conditions';
                showErrorMessage(this, message, error);
                this.$_handleLoaderState(false);
            }
        },
        async addToCondition() {
            this.$v.condition.$touch();

            if (this.validateRule()) {
                return;
            }
            
            const originalConditionLength = this.condition.length;

            if (this.condition.selectedVariable?.key === 'Tag') {
                const tagId = this.condition.tagValue?.tagId;
                if (tagId === undefined || tagId === null) {
                    return;
                }
                if (this.condition.length > 0) {
                    const tagRule = `${this.condition.selectedOperator.type} ${this.condition.selectedVariable.type}.Contains(${tagId})`;
                    this.condition.push(tagRule);
                } else {
                    const tagRule = `${this.condition.selectedVariable.type}.Contains(${tagId})`;
                    this.condition.push(tagRule);
                }
            }
            else if (this.condition.selectedOption.key === 'Between') {
                if (this.condition.length > 0) {
                    const conRule = `${this.condition.selectedOperator.type} ${
                        this.condition.selectedVariable.type
                    } >= ${this.condition.value} && ${this.condition.selectedVariable.type} <= ${
                        this.condition.valueTwo
                    }`;
                    this.condition.push(conRule);
                } else {
                    const conRule = `${this.condition.selectedVariable.type} >= ${this.condition.value} && ${
                        this.condition.selectedVariable.type
                    } <= ${this.condition.valueTwo}`;
                    this.condition.push(conRule);
                }
            } else if (this.condition.length <= 0) {
                const currentCondition = this.conditionVariables.find(
                    (x) => x.type === this.condition.selectedVariable.type
                );
                if (currentCondition.type === 'PickupAddress' || currentCondition.type === 'DropAddress') {
                    const conRule = `${this.condition.selectedVariable.type}.${this.condition.selectedOption.type}(${
                        this.condition.zoneValue.rateZoneId
                    })`;
                    this.condition.push(conRule);
                } else {
                    const conRule = `${this.condition.selectedVariable.type} ${this.condition.selectedOption.key} ${
                        this.condition.value
                    }`;
                    this.condition.push(conRule);
                }
            } else {
                const currentCondition = this.conditionVariables.find(
                    (x) => x.type === this.condition.selectedVariable.type
                );
                if (currentCondition.type === 'PickupAddress' || currentCondition.type === 'DropAddress') {
                    const conRule = `${this.condition.selectedOperator.type} ${
                        this.condition.selectedVariable.type
                    }.${this.condition.selectedOption.type}(${this.condition.zoneValue.rateZoneId})`;
                    this.condition.push(conRule);
                } else {
                    const conRule = `${this.condition.selectedOperator.type} ${
                        this.condition.selectedVariable.type
                    } ${this.condition.selectedOption.key} ${this.condition.value}`;
                    this.condition.push(conRule);
                }
            }

            if (this.condition.length > originalConditionLength) {
                this.$v.condition.$reset();
            }
        },
        deleteCondition(item) {
            const index = this.condition.indexOf(item);

            if (index === 0) {
                this.condition.splice(index, 1);

                if (this.condition.length) {
                    this.condition[0] = this.condition[0].slice(3);
                }
            } else {
                this.condition.splice(index, 1);
            }
        },
        validateRule() {
            if (this.condition.selectedOption?.key === 'Between') {
                if (this.condition.valueTwo < this.condition.value) {
                    showErrorMessage(this, 'Second Between value needs to be above the first value', null);
                    return true;
                }
            }

            if (
                this.condition.selectedVariable?.type === 'PickupAddress' ||
                this.condition.selectedVariable?.type === 'DropAddress'
            ) {
                if (this.condition.zoneValue === null || this.condition.zoneValue === undefined) {
                    showErrorMessage(this, 'Zone must be selected', null);
                    return true;
                }

                return false;
            }

            if (this.condition.selectedVariable?.type === 'TagIds') {
                if (this.condition.tagValue === null || this.condition.tagValue === undefined) {
                    showErrorMessage(this, 'Tag must be selected', null);
                    return true;
                }

                return false;
            }

            return this.$v.$invalid;
        },
        limitText(count) {
            if (count > 1) 
                return `and ${count} other zones`;
            return `and ${count} other zone`;
        }
    }
};
</script>

<style lang="scss" scoped>
$bg-soft: #f7f9fb;
$border-soft: #e2e8f0;
$primary-accent: #1976d2; // removed unused $chip-bg

.modern-rule-modal {
    max-width: 920px;
    margin: 0 auto;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    border-radius: 14px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.07), 0 12px 32px rgba(0,0,0,0.08);
    overflow: visible;
}

.enhanced-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 20px 28px 10px;
    background: linear-gradient(135deg, #ffffff 0%, #f5f9ff 100%);
    border-bottom: 1px solid $border-soft;
}

.title-block {
    display:flex;
    gap:14px;
    align-items:flex-start;
}

.title-icon {
    font-size:28px;
    color:$primary-accent;
    margin-top:2px;
}

.modal-title {
    margin:0;
    font-weight:600;
    font-size:20px;
    letter-spacing:.3px;
}

.subtitle {
    margin:2px 0 0;
    font-size:12.5px;
    color:#54637a;
    line-height:1.4;
}

 .close-btn {
    margin-top: -4px;
}

.modal-body {
    padding: 12px 28px 8px;
}

::v-deep .form-wrapper {
    width: 100%;
}

.condition-form-grid {
    display: grid;
    gap: 14px;
    align-items: end;
    padding-bottom: 4px;
}

.condition-form-grid.has-operator:not(.has-between) {
    grid-template-areas: 'op var cond val action';
    grid-template-columns: 100px 220px 180px 100px 110px;
}
.condition-form-grid.no-operator:not(.has-between) {
    grid-template-areas: 'var cond val action';
    grid-template-columns: 220px 180px 100px 110px;
}
.condition-form-grid.zone-active.has-operator:not(.has-between) {
    grid-template-columns: 100px 220px 180px 200px 110px;
}
.condition-form-grid.zone-active.no-operator:not(.has-between) {
    grid-template-columns: 220px 180px 200px 110px;
}

.condition-form-grid.has-between.has-operator {
    grid-template-areas: 'op var cond val between action';
    grid-template-columns: 100px 220px 180px 100px 100px 110px;
}
.condition-form-grid.has-between.no-operator {
    grid-template-areas: 'var cond val between action';
    grid-template-columns: 220px 180px 100px 100px 110px;
}
.condition-form-grid.zone-active.has-between.has-operator {
    grid-template-columns: 90px 220px 180px 200px 100px 110px;
}
.condition-form-grid.zone-active.has-between.no-operator {
    grid-template-columns: 220px 180px 200px 100px 110px;
}

.operator-wrapper { grid-area: op; }
.variable-segment { grid-area: var; }
.condition-segment { grid-area: cond; }
.value-zone-segment { grid-area: val; }
.between-second { grid-area: between; }
.action-segment {
    grid-area: action;
    padding-bottom: 10px;
    margin: 0;
    width: 15px;
}

.condition-form-grid .form-segment ::v-deep .vs__dropdown-toggle,
.condition-form-grid .form-segment ::v-deep .multiselect__tags { width: 100%; }

.condition-form-grid .form-segment {
    flex: initial !important;
    min-width: 0 !important;
    width: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
}
.condition-form-grid .form-segment ::v-deep .form-group { width: 100%; }
.condition-form-grid .form-segment ::v-deep .v-select { width: 100%; }
.condition-form-grid .form-segment ::v-deep .vs__dropdown-toggle { box-sizing: border-box; }
.condition-form-grid .form-segment ::v-deep .vs__selected-options { max-width: 100%; }

.variable-segment, .condition-segment, .value-zone-segment { min-width: 0; }
.variable-segment ::v-deep .vs__selected-options,
.condition-segment ::v-deep .vs__selected-options { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.variable-segment ::v-deep .vs__selected,
.condition-segment ::v-deep .vs__selected,
.value-zone-segment ::v-deep .vs__selected {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
}

.condition-form-grid .form-segment { overflow: visible; }
.zone-select { overflow: visible; }

.value-zone-segment { width: 100%; }
.variable-segment { width: 100%; }
.condition-form-grid .variable-segment {
    width: 220px !important;
    max-width: 220px !important;
    min-width: 220px !important;
    flex: none !important;
    box-sizing: border-box;
}
.condition-form-grid.no-operator .variable-segment {
    width: 220px !important;
    max-width: 220px !important;
    min-width: 220px !important;
}

.variable-segment ::v-deep .vs__dropdown-toggle { width:100% !important; }
.variable-segment ::v-deep .vs__selected-options { width:100% !important; flex:1 1 auto; }
.variable-segment ::v-deep .vs__selected { max-width:100%; display:block; overflow:hidden; text-overflow:ellipsis; }
.value-zone-segment.is-zone ::v-deep .multiselect__tags { min-height: 40px; }

.between-second { position: relative; }
.between-second .between-container ::v-deep input { transition: opacity .15s ease; }

.condition-form-grid ::v-deep .vs__dropdown-toggle,
.condition-form-grid ::v-deep .multiselect__tags,
.condition-form-grid ::v-deep input.md-input { min-height:44px; height:44px; line-height:42px; }

.condition-form-grid ::v-deep .vs__dropdown-toggle { display:flex; align-items:center; padding-top:0; padding-bottom:0; }
.condition-form-grid ::v-deep .vs__selected-options { display:flex; align-items:center; }
.condition-form-grid ::v-deep .vs__selected { margin:0 !important; line-height:1.2; display:flex; align-items:center; }
.condition-form-grid ::v-deep .vs__search { margin:0; padding:0; line-height:1.2; }
.condition-form-grid ::v-deep .multiselect__tags { display:flex; align-items:center; }
.condition-form-grid ::v-deep .multiselect__single { display:flex; align-items:center; line-height:1.2; margin:0; }
.condition-form-grid ::v-deep .multiselect__input { padding-top:0 !important; padding-bottom:0 !important; margin:0 !important; }

.condition-form-grid ::v-deep input.md-input { line-height:44px; }

.condition-form-grid ::v-deep .vs__dropdown-toggle,
.condition-form-grid ::v-deep .multiselect__tags { overflow: hidden; }
.condition-form-grid ::v-deep .vs__selected,
.condition-form-grid ::v-deep .multiselect__single,
.condition-form-grid ::v-deep .multiselect__tag {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}

.condition-form-grid ::v-deep .multiselect__tags { display:flex; align-items:center; flex-wrap:nowrap; padding:0; }
.condition-form-grid ::v-deep .multiselect__tag {
    margin: 0 4px 0 0;
    max-height: 28px;
    line-height: 26px;
}
.condition-form-grid ::v-deep .multiselect__input { padding-top: 0 !important; }

.condition-form-grid .add-btn { height: 40px; padding: 0 16px; width: 100%; justify-content:center; }

.zone-no-label ::v-deep label { display: none; }

.zone-select ::v-deep .multiselect__tags {
    border: none !important;
    background: transparent !important;
    padding-left: 10px;
    min-height: 42px;
    box-shadow: none !important;
}

.zone-select ::v-deep .multiselect__select { display: none; }
.zone-select ::v-deep .multiselect__content-wrapper { border: 1px solid #d9e2ec; }
.zone-select ::v-deep .multiselect__input { padding-top: 6px; }
.zone-select ::v-deep .multiselect__tags:hover { background: transparent !important; }

.zone-select { position: relative; z-index: 20; }
.zone-select ::v-deep .multiselect__content-wrapper {
    position: absolute !important;
    top: calc(100% + 4px);
    left: 0;
    width: 100%;
    z-index: 4000;
    box-shadow: 0 8px 18px rgba(0,0,0,0.12);
    border: 1px solid #d9e2ec;
    background: #ffffff;
    border-radius: 10px;
    overflow: auto;
    max-height: 260px !important;
}

.zone-select ::v-deep .multiselect__option--highlight { background:#e8f4ff; color:#0b4566; }
.zone-select ::v-deep .multiselect__option--selected { background:#d2ecff; color:#09394f; }

 .form-segment {
    flex: 1 1 210px;
    min-width: 180px;
}

.form-segment.small {
    flex: 0 0 150px;
}

.form-segment.medium {
    flex: 1 1 220px;
}

::v-deep .form-group {
    margin-bottom: 4px;
}
::v-deep .form-group label {
    font-weight: 600;
    font-size: 12px;
    letter-spacing: .5px;
    text-transform: uppercase;
    color: #445268;
}

::v-deep .vs__dropdown-toggle, ::v-deep .multiselect__tags {
    border-radius: 8px;
    min-height: 44px;
    border-color: $border-soft;
    background: #fff;
}

::v-deep .vs__selected, ::v-deep .multiselect__single {
    font-size: 13px;
}

::v-deep input.md-input {
    border-radius: 8px !important;
    min-height: 44px;
    padding: 0 12px;
}

 .add-btn { display:inline-flex; align-items:center; background:rgb(76,175,90)!important; color:#fff!important; height:30px!important; min-width:30px!important; }
 .add-btn span, .add-btn md-icon { color:#2c9e22 !important; }

.add-btn:hover {
    background: #41b15c !important;
    box-shadow: 0 3px 6px rgba(0,0,0,0.12), 0 6px 14px -4px rgba(65,177,92,0.45);
}

.add-btn:active {
    background: #2c7d3f !important;
    transform: translateY(1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.14);
}

.add-btn:focus-visible {
    outline: 2px solid #91e4a8;
    outline-offset: 2px;
}

.add-btn[disabled], .add-btn[aria-disabled='true'] {
    background: #aacbb4 !important;
    box-shadow: none;
    cursor: not-allowed;
    opacity: .7;
}

.rules-summary {
    margin-bottom: 20px;
    background: #ffffff;
    border: 1px solid $border-soft;
    border-radius: 12px;
    padding: 14px 16px 8px;
    position: relative;
    box-shadow: 0 2px 4px rgba(0,0,0,0.04);
}

.rules-summary-bottom {
    margin-top: 24px;
    margin-bottom: 4px;
}

.rules-summary-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    justify-self: left;
}

.rules-summary-icon {
    font-size: 20px;
    color: $primary-accent;
}

.rules-summary-title {
    font-weight: 600;
    font-size: 14px;
    letter-spacing: .4px;
    color: #2d4258;
}

.rules-count-badge {
    background: #e3f3ff;
    color: #0b6aa7;
    font-size: 11px;
    padding: 2px 8px 3px;
    border-radius: 14px;
    font-weight: 600;
    letter-spacing: .5px;
    text-transform: uppercase;
}

.rules-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 180px;
    overflow-y: auto;
}

.rules-list-inline {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 10px 12px;
    height: 180px;
    max-height: 200px;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 4px;
    align-content: flex-start;
}

.rules-list-inline::-webkit-scrollbar { width: 8px; }
.rules-list-inline::-webkit-scrollbar-track { background: transparent; }
.rules-list-inline::-webkit-scrollbar-thumb { background: #d2dbe2; border-radius: 10px; }
.rules-list-inline::-webkit-scrollbar-thumb:hover { background: #b9c5cd; }

.rules-list-inline { scrollbar-width: thin; scrollbar-color: #b9c5cd transparent; }

.rule-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 6px 10px 6px 12px;
    background: #f8fafb;
    border: 1px solid #e5edf3;
    border-radius: 10px;
    position: relative;
    font-size: 13px;
    line-height: 1.3;
    transition: background .15s ease, border-color .15s ease;
}

.rule-item-inline {
    align-items: center;
    padding: 6px 14px 6px 14px;
    border-radius: 24px;
    background: #f5f8fa;
}

.rule-item-inline .rule-text { font-weight: 500; }

.rule-item:hover {
    background: #f1f6f9;
    border-color: #d5e2ec;
}

.rule-text {
    flex: 1;
    color: #1d2b3a;
    font-weight: 500;
}

.rule-delete-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 6px;
    color: #5d6b78;
    transition: background .15s ease, color .15s ease;
}

.rule-delete-btn md-icon { font-size: 16px; }

.rule-delete-btn:hover {
    background: #ffe8e6;
    color: #c62828;
}

.rule-delete-btn:focus-visible {
    outline: 2px solid #ffb4ae;
    outline-offset: 2px;
}

.rules-list::-webkit-scrollbar { width: 8px; }
.rules-list::-webkit-scrollbar-track { background: transparent; }
.rules-list::-webkit-scrollbar-thumb { background: #d2dbe2; border-radius: 10px; }
.rules-list::-webkit-scrollbar-thumb:hover { background: #b9c5cd; }

 .modern-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 24px 18px;
    border-top: 1px solid $border-soft;
    background: #ffffff;
}

.footer-actions {
    display: flex;
    gap: 10px;
}

.summary-count {
    font-size: 12.5px;
    color: #5a6b80;
    background: $bg-soft;
    padding: 4px 10px;
    border-radius: 20px;
}

::v-deep .vs__dropdown-toggle:focus,
::v-deep .multiselect__tags:focus,
::v-deep input.md-input:focus {
    outline: 2px solid rgba(25, 118, 210, .35);
    box-shadow: 0 0 0 3px rgba(25, 118, 210, .15);
}

.select-value-box ::v-deep .vs__placeholder,
.select-value-box ::v-deep .vs__search::placeholder {
    color: #AAAAAA !important;
    font-weight: 200;
    font-size: 14px;
}

.select-value-box:focus,
.select-value-box:focus-visible,
.select-value-box.md-input:focus {
    outline: none !important;
    box-shadow: none !important;
    border-color: transparent !important;
}

.select-value-box ::v-deep .vs__dropdown-toggle:focus,
.select-value-box ::v-deep .vs__dropdown-toggle:focus-visible {
    outline: none !important;
    box-shadow: none !important;
    border-color: #d9e2ec !important;
}

</style>